import { inject } from '@angular/core';
import { EventService, FaqService, GroupService, NewsService, SurveyService, TaskService } from '@core/services';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageConversionService } from '@core/services/image-conversion.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { FilterQuery } from '@core/types';
import { MyContent } from '@core/types/MyContent.type';
import {
  FaqEntity,
  Pagination,
  UserContentEventEntity,
  UserContentEventList,
  UserContentFaqList,
  UserContentGroupEntity,
  UserContentGroupList,
  UserContentNewsEntity,
  UserContentNewsList,
  UserContentSurveyEntity,
  UserContentSurveyList,
  UserContentTaskEntity,
  UserContentTaskList,
} from '@core/entities';
import { GenerateImageUrlWithToken } from '@core/utils';
import { SectionType } from '@core/enums';

export class UseMyContent {
  private readonly _newsService = inject(NewsService);
  private readonly _eventService = inject(EventService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _taskService = inject(TaskService);
  private readonly _groupService = inject(GroupService);
  private readonly _faqService = inject(FaqService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _imageConversionService = inject(ImageConversionService);

  getAllNews(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentNewsList> {
    return this._newsService.getContentNews(pagination, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            news: response.news.map((item: UserContentNewsEntity) => {
              const transformedItem = plainToClass(UserContentNewsEntity, item);
              if (transformedItem.id) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.NEWS);
              }
              return transformedItem;
            }),
          } as UserContentNewsList;
        }
        return null;
      }),
    );
  }

  getAllEvents(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentEventList> {
    return this._eventService.getContentEvents(pagination, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            events: response.events.map((item: UserContentEventEntity) => {
              const transformedItem = plainToClass(UserContentEventEntity, item);
              if (transformedItem.id) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.EVENT);
              }
              return transformedItem;
            }),
          } as UserContentEventList;
        }
        return null;
      }),
    );
  }

  getAllSurveys(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentSurveyList> {
    return this._surveyService.getContentSurveys(pagination, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            survey: response.survey.map((item) => {
              return plainToClass(UserContentSurveyEntity, item);
            }),
          } as UserContentSurveyList;
        }
        return null;
      }),
    );
  }

  getAllTasks(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentTaskList> {
    return this._taskService.getContentTasks(pagination, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            tasks: response.tasks.map((item: UserContentTaskEntity) => {
              const transformedItem = plainToClass(UserContentTaskEntity, item);
              if (transformedItem.id) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.TASK);
              }
              return transformedItem;
            }),
          } as UserContentTaskList;
        }
        return null;
      }),
    );
  }

  getAllGroups(queryParams?: FilterQuery): Observable<UserContentGroupList> {
    return this._groupService.getContentGroups(queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            groups: response.groups.map((item: UserContentGroupEntity) => {
              const transformedItem = plainToClass(UserContentGroupEntity, item);
              if (transformedItem && transformedItem.id) {
                transformedItem.imageUrl = GenerateImageUrlWithToken(transformedItem.id, SectionType.GROUP);
              }
              return transformedItem;
            }),
          } as UserContentGroupList;
        }
        return null;
      }),
    );
  }

  getAllFaqs(pagination: Partial<Pagination>, queryParams?: FilterQuery): Observable<UserContentFaqList> {
    return this._faqService.getContentFaqs(pagination, queryParams).pipe(
      map((response) => {
        if (response) {
          return {
            pagination: response.pagination,
            faqs: response.faqs.map((item: FaqEntity) => {
              const transformedItem = plainToClass(FaqEntity, item);
              if (transformedItem.image) {
                transformedItem.image = GenerateImageUrlWithToken(transformedItem.id, SectionType.FAQ);
              }
              return transformedItem;
            }),
          } as UserContentFaqList;
        }
        return null;
      }),
    );
  }

  // ----------- AUX -------------

  convertBase64ToImage(image: string) {
    if (!image) {
      return null;
    }
    const base64Data = image.substring(20);
    return this._sanitizer.bypassSecurityTrustUrl(this._imageConversionService.convertBase64ToBlobUrl(base64Data)) as string;
  }

  transformNewsToMyContent(news: UserContentNewsEntity[]): MyContent[] {
    return news.map((news) => {
      return {
        ...news,
        image: news.imageUrl,
      };
    });
  }

  transformEventsToMyContent(events: UserContentEventEntity[]): MyContent[] {
    return events.map((event) => {
      return {
        ...event,
        image: event.imageUrl,
      };
    });
  }

  transformTasksToMyContent(tasks: UserContentTaskEntity[]): MyContent[] {
    return tasks.map((task) => {
      return {
        ...task,
        image: task.imageUrl ?? '',
      };
    });
  }

  transformGroupsToMyContent(groups: UserContentGroupEntity[]): MyContent[] {
    return groups.map((group) => {
      return {
        ...group,
        image: group.imageUrl ?? '',
      };
    });
  }

  transformFaqsToMyContent(faqs: FaqEntity[]): MyContent[] {
    return faqs.map((faq) => {
      return {
        ...faq,
        image: faq.image ?? '',
      };
    });
  }

  transformGetFaqByIdToMyContent(faq: FaqEntity): MyContent {
    return {
      ...faq,
      image: faq.image ?? '',
    };
  }
}
